import { useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import type { ProductDto } from "../api/types";
import { useAppState, useScanner } from "../state/appState";
import { useSession } from "../state/session";
import { useToasts } from "../state/toasts";
import { Empty, Modal } from "../ui/components";
import { money } from "../ui/format";

/**
 * Касса: заказ, который сотрудник набирает у окна.
 *
 * За наличные набор состава и приём денег — одно действие: кассир и старший
 * смены на точке один человек. С подключённым терминалом расчёт закрывает
 * ответ устройства, и экран ждёт его, не давая набрать следующий заказ.
 *
 * Это раздел рабочего места смены, а не отдельный экран: каркас с шапкой и
 * переходами даёт `CounterLayout`, здесь остаётся только сама касса.
 */

interface Chosen {
  groupId: string;
  optionId: string;
  title: string;
  price: number;
  qty: number;
}

interface CartLine {
  productId: string;
  title: string;
  unitPrice: number;
  qty: number;
  modifiers: Chosen[];

  /**
   * Коды маркировки — по одному на бутылку.
   *
   * У маркированного товара количество не набирается кнопками: сколько кодов
   * отсканировано, столько штук и продаётся. Иначе в чек уехала бы бутылка,
   * которой никто не прикладывал к сканеру.
   */
  codes?: string[];

  /**
   * Номер товара (GTIN) бутылки — режим общепита.
   *
   * Кода экземпляра нет, есть номер товара: он уходит в чек вместо кода,
   * и количество набирается кнопками, как у любого блюда. Номер выбирает
   * скан полосатого кода; у позиции с одной маркой сервер подставит его сам.
   */
  gtin?: string;
}

/** Ответ проверки кода маркировки. */
interface MarkingCheck {
  allowed: boolean;
  refusal: string | null;
  reason: string;
  printView: string | null;
  warning: string | null;
  offline: boolean;
}

/**
 * Код со сканера — в Base64, как его принимают и модуль проверки, и касса.
 *
 * Внутри кода служебные разделители, и передавать его обычной строкой нельзя.
 * Символ вне однобайтового диапазона означает, что сканер отдал не то: лучше
 * честно отказаться, чем отправить на проверку испорченный код.
 */
function toBase64(raw: string): string | null {
  let binary = "";
  for (const char of raw) {
    const code = char.codePointAt(0) ?? 0;
    if (code > 255) return null;
    binary += String.fromCharCode(code);
  }
  return binary ? btoa(binary) : null;
}

/**
 * Номер товара из кода маркировки: `01` и четырнадцать цифр в самом начале.
 *
 * По нему бутылка, отсканированная до выбора товара, сама находит свою
 * позицию в меню. Разбор нарочно грубый — всё остальное проверит сервер;
 * здесь нужно лишь понять, о каком товаре речь.
 */
function gtinOfScan(code: string): string | null {
  let raw = "";
  try {
    raw = atob(code);
  } catch {
    return null;
  }
  // Квадратный код маркировки: номер — четырнадцать цифр после «01».
  const marking = /^01(\d{14})/.exec(raw);
  if (marking) return marking[1];
  // Полосатый EAN: сами цифры. В общепите его и сканируют — квадрат не нужен.
  const plain = raw.trim();
  return /^\d{8,14}$/.test(plain) ? plain.padStart(14, "0") : null;
}

export function CounterPage() {
  const { snapshot } = useAppState();
  const { can } = useSession();
  const { notify } = useToasts();

  const [lines, setLines] = useState<CartLine[]>([]);
  const [picking, setPicking] = useState<ProductDto | null>(null);
  const [scanning, setScanning] = useState<{
    product: ProductDto;
    modifiers: Chosen[];
    /** Код, отсканированный до того, как окно открылось: проверить сразу. */
    pending?: string;
  } | null>(null);

  const [category, setCategory] = useState<string>("all");
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [comment, setComment] = useState("");
  const [promoCode, setPromoCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [lastNumber, setLastNumber] = useState<string | null>(null);
  const [awaiting, setAwaiting] = useState<{ id: string; number: string; total: number } | null>(null);

  /*
   * Ожидание оплаты закрывает сам поток событий: как только терминал ответил,
   * заказ в снимке меняет статус. Опрашивать сервер незачем — снимок и так
   * приходит на каждое изменение.
   */
  const awaitingOrder = awaiting ? snapshot?.orders.find(order => order.id === awaiting.id) : undefined;

  useEffect(() => {
    if (!awaiting || !awaitingOrder) return;

    if (awaitingOrder.paymentStatus === "paid") {
      notify(`Заказ №${awaiting.number} оплачен · ${money(awaiting.total)}`, "ok");
      setAwaiting(null);
      return;
    }

    if (awaitingOrder.status === "payment_failed") {
      notify(`Карта отклонена по заказу №${awaiting.number}`, "error");
      setAwaiting(null);
    }
  }, [awaiting, awaitingOrder, notify]);

  const products = useMemo(
    () => (snapshot?.products ?? []).filter(product => !product.archived && product.available),
    [snapshot]
  );

  const categories = useMemo(() => {
    const used = new Map<string, string>();
    for (const product of products) used.set(product.categoryId, product.category.title);
    return [...used.entries()];
  }, [products]);

  const shown = category === "all" ? products : products.filter(product => product.categoryId === category);

  /*
   * Бутылку поднесли к сканеру, не выбрав товар.
   *
   * Так у окна и делают: бутылка в руке, сканер под рукой, тыкать в меню
   * некогда. По номеру товара из кода находим позицию сами; если позиций
   * несколько или у товара есть варианты, честно просим выбрать руками —
   * угадывать в деньгах нельзя.
   */
  const horeca = snapshot?.settings.markingMode === "horeca";

  useScanner(
    code => {
      const gtin = gtinOfScan(code);
      if (!gtin) {
        notify(
          horeca
            ? "Это не штрихкод товара: отсканируйте полосатый код с бутылки"
            : "Это не код маркировки: отсканируйте квадратный код с бутылки",
          "error"
        );
        return;
      }

      const scanned = gtin.replace(/^0+/, "");
      const matches = products.filter(
        product =>
          product.marked &&
          product.available &&
          product.gtins.some(known => known.replace(/^0+/, "") === scanned)
      );
      if (matches.length === 0) {
        notify(`Бутылки с номером ${gtin} в меню нет`, "error");
        return;
      }
      if (matches.length > 1) {
        notify("Под этот код подходит несколько позиций: выберите товар руками", "info");
        return;
      }

      const [product] = matches;
      if (product.modifiers.some(group => group.available)) {
        notify(`${product.title}: сначала выберите вариант, потом сканируйте`, "info");
        return;
      }

      /*
       * Общепит: проверять нечего, бутылка идёт прямо в корзину с номером
       * товара. Повторный скан той же марки прибавляет штуку к той же строке.
       */
      if (horeca) {
        addMarkedLine(product, [], gtin);
        return;
      }

      setScanning({ product, modifiers: [], pending: code });
    },
    scanning === null && picking === null
  );

  // Итог считается по ценам каталога и служит подсказкой кассиру: настоящую
  // сумму, со скидками и акциями, считает сервер при создании заказа.
  const preview = lines.reduce((sum, line) => sum + line.unitPrice * line.qty, 0);

  if (!can("orders.counter")) return <Empty>Нет доступа к кассе.</Empty>;
  if (!snapshot) return <Empty>Загрузка меню…</Empty>;

  /**
   * Маркированная бутылка в режиме общепита: строка с номером товара.
   *
   * Номер известен из скана или, если марка одна, из карточки товара.
   * Несколько марок и нет скана — просим сканировать: угадывать нельзя,
   * «Честный знак» спишет ту бутылку, чей номер уйдёт в чек.
   */
  function addMarkedLine(product: ProductDto, modifiers: Chosen[], gtin: string | null) {
    const chosen = gtin ?? (product.gtins.length === 1 ? product.gtins[0].padStart(14, "0") : null);

    if (!chosen) {
      notify(
        product.gtins.length === 0
          ? `${product.title}: у товара не задан номер GTIN — заполните его в меню`
          : `${product.title}: несколько марок под одной позицией — отсканируйте штрихкод бутылки`,
        "error"
      );
      return;
    }

    const unitPrice = product.price + modifiers.reduce((sum, item) => sum + item.price * item.qty, 0);
    const same = (line: CartLine) =>
      line.productId === product.id &&
      line.gtin === chosen &&
      JSON.stringify(line.modifiers) === JSON.stringify(modifiers);

    setLines(current => {
      const index = current.findIndex(same);
      if (index < 0) {
        return [
          ...current,
          { productId: product.id, title: product.title, unitPrice, qty: 1, modifiers, gtin: chosen }
        ];
      }
      return current.map((line, position) => (position === index ? { ...line, qty: line.qty + 1 } : line));
    });
  }

  function addLine(product: ProductDto, modifiers: Chosen[], qty: number) {
    setPicking(null);

    // Маркированный товар из выбора состава уходит не в корзину, а к сканеру:
    // количество ему задаёт не кнопка, а число приложенных бутылок.
    // В общепите сканера не надо: строка с номером товара.
    if (product.marked) {
      if (horeca) addMarkedLine(product, modifiers, null);
      else setScanning({ product, modifiers });
      return;
    }

    const unitPrice = product.price + modifiers.reduce((sum, item) => sum + item.price * item.qty, 0);

    setLines(current => [
      ...current,
      { productId: product.id, title: product.title, unitPrice, qty, modifiers }
    ]);
  }

  /**
   * Бутылка с принятым кодом.
   *
   * Каждый скан — это ровно одна штука: строка растёт вместе со списком кодов,
   * и разъехаться количеству с кодами не на чем.
   */
  function addScanned(product: ProductDto, modifiers: Chosen[], code: string) {
    const unitPrice = product.price + modifiers.reduce((sum, item) => sum + item.price * item.qty, 0);
    const same = (line: CartLine) =>
      line.productId === product.id &&
      Boolean(line.codes) &&
      JSON.stringify(line.modifiers) === JSON.stringify(modifiers);

    setLines(current => {
      const index = current.findIndex(same);
      if (index < 0) {
        return [
          ...current,
          { productId: product.id, title: product.title, unitPrice, qty: 1, modifiers, codes: [code] }
        ];
      }

      return current.map((line, position) =>
        position === index
          ? { ...line, qty: line.qty + 1, codes: [...(line.codes ?? []), code] }
          : line
      );
    });
  }

  function pick(product: ProductDto) {
    const groups = product.modifiers.filter(group => group.available);

    // Маркированный товар начинается со сканера, а не с корзины: без кода
    // продать его нельзя, и добавлять позицию, которую придётся снимать, незачем.
    // Если у него есть варианты, сначала выбирают их — состав должен быть
    // известен до того, как код привяжется к позиции.
    if (product.marked && groups.length === 0) {
      if (horeca) addMarkedLine(product, [], null);
      else setScanning({ product, modifiers: [] });
      return;
    }
    // Товар без выбора добавляется одним нажатием: у окна очередь, и лишний
    // экран ради «ок» стоит секунд, которых нет.
    if (groups.length === 0) {
      addLine(product, [], 1);
      return;
    }
    setPicking(product);
  }

  async function submit(paymentMethod: "cash" | "card_terminal") {
    if (lines.length === 0) return;
    setBusy(true);

    const payload = {
      paymentMethod,
      customerPhone: phone.trim() || undefined,
      customerName: name.trim() || undefined,
      comment: comment.trim() || undefined,
      promoCode: promoCode.trim() || undefined,
      items: lines.map(line => ({
        productId: line.productId,
        qty: line.qty,
        modifiers: line.modifiers.map(modifier => ({
          groupId: modifier.groupId,
          optionId: modifier.optionId,
          qty: modifier.qty
        })),
        ...(line.codes ? { markingCodes: line.codes } : {}),
        ...(line.gtin ? { gtin: line.gtin } : {})
      }))
    };

    try {
      const order = await api.once.post<{ id: string; number: string; total: number; status: string }>(
        "/api/orders/counter",
        payload
      );

      setLines([]);
      setPhone("");
      setName("");
      setComment("");
      setPromoCode("");
      setLastNumber(order.number);

      /*
       * С подключённым терминалом деньги списывает устройство, и до его ответа
       * расчёта нет. Кассир видит ожидание и не отдаёт заказ гостю раньше
       * времени; результат придёт сам — экран уже слушает поток событий.
       */
      if (order.status === "awaiting_payment") {
        setAwaiting({ id: order.id, number: order.number, total: order.total });
      } else {
        notify(`Заказ №${order.number} принят · ${money(order.total)}`, "ok");
      }
    } catch (error) {
      notify(error instanceof Error ? error.message : "Не удалось принять заказ", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="counter">
        <section className="counter-menu">
          <nav className="tabs" aria-label="Категории">
            <button
              type="button"
              className={`tab ${category === "all" ? "active" : ""}`}
              onClick={() => setCategory("all")}
            >
              Всё меню
            </button>
            {categories.map(([id, title]) => (
              <button
                key={id}
                type="button"
                className={`tab ${category === id ? "active" : ""}`}
                onClick={() => setCategory(id)}
              >
                {title}
              </button>
            ))}
          </nav>

          <div className="counter-grid">
            {shown.map(product => (
              <button key={product.id} type="button" className="counter-tile" onClick={() => pick(product)}>
                <strong>{product.title}</strong>
                <span>{money(product.price)}</span>
              </button>
            ))}
          </div>
        </section>

        <aside className="counter-cart">
          {/* Ожидание перекрывает корзину целиком: пока идёт расчёт, набирать
              следующий заказ нельзя — терминал занят этим. */}
          {awaiting ? (
            <div className="terminal-wait" role="status">
              <strong>Оплата на терминале</strong>
              <b className="num">{money(awaiting.total)}</b>
              <span className="muted">Заказ №{awaiting.number} · попросите гостя приложить карту</span>
            </div>
          ) : null}

          <header>
            <h2>Заказ</h2>
            {lastNumber ? <small className="muted">последний выдан: №{lastNumber}</small> : null}
          </header>

          {lines.length === 0 ? (
            <Empty>Выберите позиции слева.</Empty>
          ) : (
            <ul className="counter-lines">
              {lines.map((line, index) => (
                <li key={index}>
                  <div>
                    <strong>{line.title}</strong>
                    {line.codes ? (
                      <>
                        <br />
                        <small className="muted">маркировка: {line.codes.length} шт. отсканировано</small>
                      </>
                    ) : null}
                    {line.gtin ? (
                      <>
                        <br />
                        <small className="muted num">маркировка, товар {line.gtin}</small>
                      </>
                    ) : null}
                    {line.modifiers.length > 0 ? (
                      <>
                        <br />
                        <small className="muted">
                          {line.modifiers.map(modifier => modifier.title).join(", ")}
                        </small>
                      </>
                    ) : null}
                  </div>

                  <div className="row-actions">
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() =>
                        setLines(current =>
                          current
                            .map((item, position) =>
                              position === index
                                ? {
                                    ...item,
                                    qty: item.qty - 1,
                                    ...(item.codes ? { codes: item.codes.slice(0, -1) } : {})
                                  }
                                : item
                            )
                            .filter(item => item.qty > 0)
                        )
                      }
                    >
                      −
                    </button>
                    <span className="num">{line.qty}</span>
                    <button
                      type="button"
                      className="btn btn-sm"
                      onClick={() => {
                        /*
                         * У маркированной позиции количество прибавляет только
                         * сканер: лишняя штука без кода не доедет до чека,
                         * а узнал бы об этом кассир уже при оплате.
                         */
                        if (line.codes) {
                          const product = products.find(item => item.id === line.productId);
                          if (product) setScanning({ product, modifiers: line.modifiers });
                          return;
                        }

                        setLines(current =>
                          current.map((item, position) =>
                            position === index ? { ...item, qty: item.qty + 1 } : item
                          )
                        );
                      }}
                    >
                      +
                    </button>
                    <span className="num">{money(line.unitPrice * line.qty)}</span>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="field-row">
            <label className="field">
              <span>Телефон, если назвали</span>
              <input value={phone} onChange={event => setPhone(event.target.value)} inputMode="tel" />
            </label>
            <label className="field">
              <span>Имя</span>
              <input value={name} onChange={event => setName(event.target.value)} maxLength={120} />
            </label>
          </div>

          <div className="field-row">
            <label className="field">
              <span>Промокод</span>
              <input value={promoCode} onChange={event => setPromoCode(event.target.value)} maxLength={64} />
            </label>
            <label className="field">
              <span>Комментарий кухне</span>
              <input value={comment} onChange={event => setComment(event.target.value)} maxLength={200} />
            </label>
          </div>

          <p className="counter-total">
            К оплате: <strong>{money(preview)}</strong>
            <br />
            <small className="muted">Скидки и акции пересчитает сервер при приёме оплаты.</small>
          </p>

          <div className="counter-actions">
            <button
              type="button"
              className="btn btn-xl btn-primary"
              disabled={lines.length === 0 || busy || awaiting !== null}
              onClick={() => void submit("cash")}
            >
              Наличные
            </button>
            <button
              type="button"
              className="btn btn-xl btn-primary"
              disabled={lines.length === 0 || busy || awaiting !== null}
              onClick={() => void submit("card_terminal")}
            >
              Карта
            </button>
            <button
              type="button"
              className="btn"
              disabled={lines.length === 0 || busy || awaiting !== null}
              onClick={() => setLines([])}
            >
              Очистить
            </button>
          </div>
        </aside>
      </div>

      {picking ? (
        <ModifierPicker product={picking} onClose={() => setPicking(null)} onAdd={addLine} />
      ) : null}

      {scanning ? (
        <ScanBottle
          product={scanning.product}
          pending={scanning.pending}
          onClose={() => setScanning(null)}
          onScanned={code => addScanned(scanning.product, scanning.modifiers, code)}
        />
      ) : null}
    </>
  );
}

/**
 * Выбор состава для товара с вариантами.
 *
 * Правила те же, что в клиентском меню: обязательную группу нельзя пропустить,
 * иначе кухня получит заказ, который нельзя собрать.
 */
function ModifierPicker({
  product,
  onClose,
  onAdd
}: {
  product: ProductDto;
  onClose: () => void;
  onAdd: (product: ProductDto, modifiers: Chosen[], qty: number) => void;
}) {
  const groups = product.modifiers.filter(group => group.available);

  const [picked, setPicked] = useState<Record<string, string[]>>(() => {
    const initial: Record<string, string[]> = {};
    for (const group of groups) {
      const options = group.options.filter(option => option.available);
      if (group.required && options.length === 1) initial[group.id] = [options[0]!.id];
    }
    return initial;
  });
  const [qty, setQty] = useState(1);

  const missing = groups.filter(group => group.required && (picked[group.id]?.length ?? 0) < group.min);

  const chosen: Chosen[] = groups.flatMap(group =>
    (picked[group.id] ?? []).map(optionId => {
      const option = group.options.find(candidate => candidate.id === optionId)!;
      return { groupId: group.id, optionId, title: option.title, price: option.price, qty: 1 };
    })
  );

  const unitPrice = product.price + chosen.reduce((sum, item) => sum + item.price, 0);

  function toggle(groupId: string, optionId: string, max: number) {
    setPicked(current => {
      const selected = current[groupId] ?? [];
      if (selected.includes(optionId)) {
        return { ...current, [groupId]: selected.filter(item => item !== optionId) };
      }
      // Дошли до предела — новый выбор вытесняет самый старый: так у группы
      // «на один вариант» переключение работает одним нажатием.
      const next = max === 1 ? [optionId] : [...selected, optionId].slice(-max);
      return { ...current, [groupId]: next };
    });
  }

  return (
    <Modal
      title={product.title}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button
            type="button"
            className="btn btn-primary"
            disabled={missing.length > 0}
            onClick={() => onAdd(product, chosen, qty)}
          >
            {missing.length > 0
              ? `Выберите: ${missing[0]!.title}`
              : `Добавить · ${money(unitPrice * qty)}`}
          </button>
        </>
      }
    >
      {groups.map(group => (
        <div key={group.id} className="field">
          <span>
            {group.title}
            {group.required ? " · обязательно" : ""}
          </span>
          <div className="row-actions">
            {group.options
              .filter(option => option.available)
              .map(option => (
                <button
                  key={option.id}
                  type="button"
                  className={`btn btn-sm ${(picked[group.id] ?? []).includes(option.id) ? "btn-primary" : ""}`}
                  onClick={() => toggle(group.id, option.id, group.max)}
                >
                  {option.title}
                  {option.price > 0 ? ` +${money(option.price)}` : ""}
                </button>
              ))}
          </div>
        </div>
      ))}

      {/* У маркированного товара количество задаёт сканер: сколько бутылок
          приложили, столько и продаётся. Кнопки здесь только сбивали бы с толку. */}
      {product.marked ? (
        <p className="tiny muted">Дальше — скан кода: каждая бутылка отмечается отдельно.</p>
      ) : (
        <div className="row-actions">
          <button type="button" className="btn btn-sm" onClick={() => setQty(value => Math.max(1, value - 1))}>
            −
          </button>
          <strong className="num">{qty}</strong>
          <button type="button" className="btn btn-sm" onClick={() => setQty(value => Math.min(50, value + 1))}>
            +
          </button>
        </div>
      )}
    </Modal>
  );
}

/**
 * Скан кода маркировки.
 *
 * Сканер работает клавиатурой: он «печатает» код в поле и жмёт Enter. Поэтому
 * здесь обычный ввод с автофокусом, а не кнопка «сканировать» — кассир только
 * подносит бутылку.
 *
 * Ответ приходит один: продавать можно или нельзя и почему. Отказ показывается
 * словами и не закрывает окно — гостю предлагают другую бутылку, и следующий
 * скан идёт в то же поле.
 */
function ScanBottle({
  product,
  pending,
  onClose,
  onScanned
}: {
  product: ProductDto;
  /** Код, отсканированный до открытия окна: проверяется сразу. */
  pending?: string;
  onClose: () => void;
  onScanned: (code: string) => void;
}) {
  const { notify } = useToasts();
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  /*
   * Сканер на мини-ПК: код приезжает живым потоком, а не клавиатурой.
   * Поле ввода остаётся — на случай сканера, воткнутого в планшет, —
   * но обычный путь теперь этот.
   */
  useScanner(code => void check(code), !busy);

  useEffect(() => {
    if (pending) void check(pending);
    // Только при открытии: код, с которым окно открыли, проверяется один раз.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function checkTyped() {
    const code = toBase64(raw.trim());
    if (!code) {
      setRefusal("Код не распознан: отсканируйте ещё раз");
      setRaw("");
      return;
    }
    void check(code);
  }

  async function check(code: string) {
    setBusy(true);
    setRefusal(null);

    try {
      const result = await api.post<MarkingCheck>("/api/marking/check", {
        code,
        productId: product.id
      });

      if (!result.allowed) {
        setRefusal(result.reason || "Продавать эту бутылку нельзя");
        setRaw("");
        return;
      }

      /*
       * Окно закрывается сразу — у окна очередь. Поэтому предупреждение
       * («серая зона», проверка по локальной базе) уходит в общее уведомление,
       * а не остаётся на закрытой карточке, где его никто не увидит.
       */
      const note = result.warning ?? (result.offline ? "Код проверен по локальной базе: связи нет" : null);
      if (note) notify(`${product.title}: ${note}`, "info");

      onScanned(code);
      onClose();
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : "Проверка не удалась");
      setRaw("");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal
      title={`${product.title}: скан кода`}
      onClose={onClose}
      actions={
        <>
          <button type="button" className="btn" onClick={onClose}>
            Отмена
          </button>
          <button type="button" className="btn btn-primary" disabled={busy || !raw.trim()} onClick={checkTyped}>
            {busy ? "Проверяем…" : "Проверить"}
          </button>
        </>
      }
    >
      <label className="field">
        <span>Поднесите бутылку к сканеру</span>
        <input
          autoFocus
          value={raw}
          disabled={busy}
          onChange={event => setRaw(event.target.value)}
          onKeyDown={event => {
            // Сканер заканчивает ввод переводом строки — это и есть «проверить».
            if (event.key === "Enter") {
              event.preventDefault();
              checkTyped();
            }
          }}
          placeholder="код с этикетки"
        />
        <small className="muted">
          Продать бутылку без проверки кода нельзя: это требование системы маркировки, а не наша осторожность.
        </small>
      </label>

      {refusal ? (
        <p className="tiny" role="alert">
          <strong>Продавать нельзя:</strong> {refusal}
        </p>
      ) : null}
    </Modal>
  );
}
